// Networking: the signalling socket and the encrypted WebRTC transfer.
import * as C from './core.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';

export const CHUNK = C.CHUNK;
const STUN = [{ urls: 'stun:stun.cloudflare.com:3478' }];
const MAX_BUFFER = 4 * 1024 * 1024;
const OFFER_MAX_AGE = 2 * 60 * 1000;
const CONSENT_WAIT = 150 * 1000;   // how long the receiver has to tap "Connect"
const IDLE_LIMIT = 60 * 1000;      // no data for this long during a transfer = stop
const MAX_PENDING = 3;             // connection requests waiting at once

// The access code never travels in a URL. It goes as a hash in the WebSocket
// sub-protocol header, so the server can turn away strangers before doing any work.
export function codeToken(code) {
  return 'drop.c.' + bytesToHex(sha256(utf8ToBytes('drop/access/' + code))).slice(0, 48);
}
// Ask to join an invite-only server. Signed by this device's key, so approval reaches this device.
export async function requestAccess(id, note) {
  const ts = Date.now(), n = String(note || '').trim().slice(0, 140);
  const sig = C.sign(id.secret, 'relay/request', id.pub, n, String(ts));
  const r = await fetch('/request-access', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pub: C.b64.enc(id.pub), note: n, ts, sig: C.b64.enc(sig) }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Your request could not be sent. Try again.');
  return j;
}

// Invite codes look like RELAY-AB2CD-EF3GH. Case, spaces and dashes don't matter.
export function inviteToken(code) {
  const raw = String(code).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^RELAY/, '');
  return 'drop.i.' + bytesToHex(sha256(utf8ToBytes('relay/invite/' + raw))).slice(0, 48);
}

// ---------------------------------------------------------------- signalling
export class Signal {
  constructor({ id, inbox, role, code, onMsg, onState }) {
    Object.assign(this, { id, inbox, role, code, onMsg, onState });
    this.ws = null; this.closed = false; this.retry = 0; this.readyP = null; this.denied = false; this.elsewhere = false;
    this.iceWait = []; this.iceCache = null;
  }
  // Ask the server for relay (TURN) details. Falls back to direct-only after 4 seconds.
  async ice() {
    if (this.iceCache && Date.now() - this.iceCache.at < 30 * 60 * 1000) return this.iceCache.servers;
    const servers = await new Promise((res) => {
      const t = setTimeout(() => res(null), 4000);
      this.iceWait.push((v) => { clearTimeout(t); res(v); });
      this.send({ t: 'ice' });
    });
    if (servers) this.iceCache = { at: Date.now(), servers };
    return servers;
  }
  url() {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/ws?inbox=${this.inbox}&role=${this.role}`;
  }
  connect() {
    this.readyP = new Promise((resolve, reject) => {
      const protos = ['drop'];
      if (this.code) protos.push(codeToken(this.code), inviteToken(this.code));
      const ws = new WebSocket(this.url(), protos);
      this.ws = ws;
      let ready = false;
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        if (!m || typeof m !== 'object') return;
        if (m.t === 'challenge' && typeof m.nonce === 'string') {
          const sig = C.sign(this.id.secret, 'drop/ws-auth', m.nonce, this.inbox, this.role);
          ws.send(JSON.stringify({ t: 'auth', pub: C.b64.enc(this.id.pub), sig: C.b64.enc(sig) }));
        } else if (m.t === 'ice') {
          const list = Array.isArray(m.iceServers) && m.iceServers.length ? m.iceServers : null;
          this.iceWait.splice(0).forEach((f) => f(list));
        } else if (m.t === 'ready') {
          ready = true; this.retry = 0; this.pending = false; this.onState?.('online', m); resolve(m);
        } else if (m.t === 'error') {
          if (m.code === 'pending') { this.pending = true; this.deniedMsg = m.msg; }
          else if (m.code === 'access' || m.code === 'blocked') { this.closed = true; this.denied = true; this.deniedMsg = m.msg; }
          this.onState?.('error', m);
        } else this.onMsg?.(m);
      };
      ws.onclose = (ev) => {
        if (ev.code === 4000) { this.closed = true; this.elsewhere = true; }
        if (!ready) reject(new Error(this.denied || this.pending ? (this.deniedMsg || 'This server needs the right access code.') : (ev.reason || 'Could not reach the server')));
        if (this.denied) return;
        this.onState?.(this.elsewhere ? 'elsewhere' : this.pending ? 'pending' : 'offline', this.pending ? { msg: this.deniedMsg } : ev);
        if (!this.closed && this.role === 'owner') {
          // Waiting for approval: check again every 10 seconds, so approval shows up quickly.
          const wait = this.pending ? 10000 : Math.min(30000, 1000 * 2 ** this.retry++);
          setTimeout(() => !this.closed && this.connect().catch(() => {}), wait);
        }
      };
    });
    return this.readyP;
  }
  send(o) { this.ws?.readyState === 1 && this.ws.send(JSON.stringify(o)); }
  close() { this.closed = true; try { this.ws?.close(); } catch {} }
}

// Wait for connection routes to be found. Stops early once a relay route has turned
// up (that's the slowest kind, and the one that always works), or after 4 seconds.
function waitIce(pc, ms = 4000) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((res) => {
    let early;
    const done = () => { clearTimeout(t); clearTimeout(early); res(); };
    const t = setTimeout(done, ms);
    pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') done(); });
    pc.addEventListener('icecandidate', (e) => { if (e.candidate?.type === 'relay' && !early) early = setTimeout(done, 300); });
  });
}

export async function routeOf(pc) {
  try {
    const stats = await pc.getStats();
    let pair; stats.forEach((s) => { if (s.type === 'transport' && s.selectedCandidatePairId) pair = stats.get(s.selectedCandidatePairId); });
    if (!pair) stats.forEach((s) => { if (s.type === 'candidate-pair' && s.state === 'succeeded' && (s.nominated || s.selected)) pair = s; });
    if (!pair) return 'Direct';
    const l = stats.get(pair.localCandidateId), r = stats.get(pair.remoteCandidateId);
    const types = [l?.candidateType, r?.candidateType];
    if (types.includes('relay')) return 'Via secure server, encrypted';
    if (types.every((t) => t === 'host')) return 'Direct, same network';
    return 'Direct over the internet';
  } catch { return 'Direct'; }
}

// Relay servers from the server if it has them; otherwise direct connections only.
async function rtcConfig(sig, relayOnly) {
  const relay = await sig.ice();
  if (relayOnly && !relay) throw new Error('“Hide my network address” is on, but this server has no secure server set up. Turn the setting off in Receive to connect directly.');
  return { iceServers: relay || STUN, iceTransportPolicy: relayOnly ? 'relay' : 'all' };
}

// Self-test: two connections on this device, both forced through the relay, send 1 MB
// to each other. Proves the relay works from the network you're on right now.
export async function testRelay(sig) {
  const relay = sig && await sig.ice();
  if (!relay) return { ok: false, why: 'This server has no secure server set up.' };
  const cfg = { iceServers: relay, iceTransportPolicy: 'relay' };
  const a = new RTCPeerConnection(cfg), b = new RTCPeerConnection(cfg);
  const t0 = performance.now();
  try {
    const dc = a.createDataChannel('t'); dc.binaryType = 'arraybuffer';
    const got = new Promise((res) => { b.ondatachannel = (e) => { let n = 0; e.channel.onmessage = (m) => { n += m.data.byteLength; if (n >= 1 << 20) res(); }; }; });
    await a.setLocalDescription(await a.createOffer()); await waitIce(a);
    if (!/typ relay/.test(a.localDescription.sdp)) throw new Error('The secure server could not be reached from this network.');
    await b.setRemoteDescription(a.localDescription);
    await b.setLocalDescription(await b.createAnswer()); await waitIce(b);
    await a.setRemoteDescription(b.localDescription);
    const fail = timeout(20000, 'The secure server could not be reached from this network.');
    await Promise.race([new Promise((r) => { dc.onopen = r; }), fail]);
    const tc = performance.now();
    const chunk = new Uint8Array(16 * 1024);
    for (let i = 0; i < 64; i++) { while (dc.bufferedAmount > 1 << 20) await new Promise((r) => setTimeout(r, 20)); dc.send(chunk); }
    await Promise.race([got, fail]); fail.clear();
    const secs = (performance.now() - tc) / 1000;
    return { ok: true, route: await routeOf(a), connectMs: Math.round(tc - t0), mbps: +(1 / secs).toFixed(1) };
  } catch (e) { return { ok: false, why: e.message }; }
  finally { a.close(); b.close(); }
}

function timeout(ms, msg) { let t; const p = new Promise((_, rej) => { t = setTimeout(() => rej(new Error(msg)), ms); }); p.clear = () => clearTimeout(t); return p; }
const isB64 = (s, max = 200000) => typeof s === 'string' && s.length <= max;

// ---------------------------------------------------------------- sending
// Returns straight away with { cancel, finished }.
// cb: { onStatus(text), onConnected({route, words, tx, total}), onConfirm(n, total), onDone(result), onError(err) }
export function sendFiles({ id, toAddress, files, code, relayOnly = false, cb, onOffline }) {
  let pc, dc, sig, done = false, stage = 'start';
  const fail = (e) => { if (done) return; done = true; cleanup(); cb.onError?.(e); };
  const cleanup = () => { try { dc?.close(); } catch {} try { pc?.close(); } catch {} sig?.close(); };
  const finished = run().catch(fail);
  return { cancel: () => fail(new Error('Cancelled.')), finished };

  async function run() {
    const toPub = C.pubFromAddress(toAddress);
    if (!files.length || files.length > C.LIMITS.files) throw new Error('Choose between 1 and 1000 files.');
    const eph = C.newEphemeral();
    let answerP = {}; const answer = new Promise((res, rej) => { answerP = { res, rej }; });
    let ownerUp = {}; const ownerOnline = new Promise((res) => { ownerUp.res = res; });
    sig = new Signal({
      id, inbox: C.inboxId(toPub), role: 'guest', code,
      onState: (s, m) => {
        if (s === 'online' && m?.ownerOnline) ownerUp.res();
        if (s === 'error') fail(new Error(m?.msg || 'The server refused the connection.'));
        if (s === 'offline' && stage !== 'direct') fail(new Error('Lost the connection to the server. Try again.'));
      },
      onMsg: (m) => {
        if (m.t === 'owner-online') ownerUp.res();
        else if (m.t === 'offline' && stage === 'waiting') cb.onStatus?.('Their device went offline. Keep this screen open and it will send when they come back.');
        else if (m.t === 'msg' && m.data && typeof m.data === 'object') {
          if (m.data.kind === 'answer') answerP.res(m.data);
          else if (m.data.kind === 'reject') answerP.rej(new Error(m.data.why === 'busy' ? 'Their device is busy with other requests. Try again in a minute.' : 'They didn’t let you connect, or only accept saved addresses.'));
        }
      },
    });
    cb.onStatus?.('Finding their device…');
    const ready = await sig.connect();
    if (done) return;
    if (!ready.ownerOnline) {
      cb.onStatus?.('Their device is offline. Keep this screen open and it will send when they come online.');
      // Doorbell: leave a sealed note at their inbox so they know you're waiting.
      try { const box = await onOffline?.(); if (box && !done) sig.send({ t: 'bell', box }); } catch {}
    }
    stage = 'waiting';
    await ownerOnline;
    if (done) return;

    stage = 'offer';
    cb.onStatus?.('Connecting…');
    pc = new RTCPeerConnection(await rtcConfig(sig, relayOnly));
    if (done) return;
    dc = pc.createDataChannel('drop', { ordered: true });
    dc.binaryType = 'arraybuffer';
    await pc.setLocalDescription(await pc.createOffer());
    await waitIce(pc);
    if (done) return;
    const sdp = pc.localDescription.sdp, ts = Date.now(), nonce = C.randomBytes(16);
    const s = C.signOffer(id, eph, toPub, sdp, ts, nonce);
    sig.send({ t: 'msg', data: { kind: 'offer', v: C.PROTOCOL, eph: C.b64.enc(eph.pub), sdp, ts, nonce: C.b64.enc(nonce), sig: C.b64.enc(s) } });
    cb.onStatus?.('Waiting for them to let you connect…');
    const tA = timeout(CONSENT_WAIT + 15000, 'They did not answer. Check the address and that Relay is open on their device.');
    const a = await Promise.race([answer, tA]); tA.clear();
    if (done) return;
    if (!isB64(a.eph, 100) || typeof a.sdp !== 'string' || !isB64(a.sig, 200)) throw new Error('The reply was malformed. Transfer stopped.');
    const ephB = C.b64.dec(a.eph);
    if (ephB.length !== 32 || !C.verifyAnswer(toPub, id.pub, eph.pub, ephB, a.sdp, C.b64.dec(a.sig))) throw new Error('The reply was not signed by that address. Transfer stopped.');
    await pc.setRemoteDescription({ type: 'answer', sdp: a.sdp });
    const tO = timeout(30000, 'Could not connect. One of the networks blocks it' + (relayOnly ? '.' : ', and no relay was available.'));
    await Promise.race([new Promise((res) => { dc.onopen = res; }), tO]); tO.clear();
    stage = 'direct';
    sig.close();
    if (done) return;

    const keys = C.sessionKeys(eph, ephB, id.pub, toPub, eph.pub, ephB);
    const out = await new C.Cipher(keys.a2b, 1).init();
    const inn = await new C.Cipher(keys.b2a, 2).init();
    const manifest = { v: C.PROTOCOL, chunk: CHUNK, files: files.map((f) => ({ name: f.name, size: f.size, type: f.type || 'application/octet-stream' })) };
    const total = files.reduce((n, f) => n + C.chunksFor(f.size), 0);
    let acks = 0, waiter = null, phase = 'manifest';
    const want = (types, ms, msg) => new Promise((res, rej) => {
      const t = setTimeout(() => rej(new Error(msg)), ms);
      waiter = { types, res: (f) => { clearTimeout(t); waiter = null; res(f); } };
    });
    let chain = Promise.resolve();
    dc.onmessage = (ev) => { chain = chain.then(async () => {
      if (done) return;
      const f = await inn.open(new Uint8Array(ev.data));
      if (f.type === C.FRAME.ACK && phase === 'sending') {
        if (++acks > total) throw new Error('bad ack');
        cb.onConfirm?.(acks, total); return;
      }
      if (waiter && waiter.types.includes(f.type)) return waiter.res(f);
      throw new Error('unexpected frame');
    }).catch(() => fail(new Error('A message failed its security check. Transfer stopped.'))); };
    dc.onclose = () => fail(new Error('The connection closed before the transfer finished.'));

    const mBytes = C.enc.json(manifest);
    const tx = C.txId(mBytes, eph.pub);
    const route = await routeOf(pc);
    cb.onConnected?.({ route, words: C.checkWords(id.pub, toPub), tx, total });
    const respP = want([C.FRAME.ACCEPT, C.FRAME.DECLINE], 5 * 60 * 1000, 'They did not accept in time.');
    dc.send(await out.seal(C.FRAME.MANIFEST, mBytes));
    cb.onStatus?.('Waiting for them to accept…');
    const resp = await respP;
    if (resp.type === C.FRAME.DECLINE) throw Object.assign(new Error('They declined the transfer.'), { declined: true });

    phase = 'sending';
    cb.onStatus?.('Sending…');
    const hashes = [];
    dc.bufferedAmountLowThreshold = MAX_BUFFER / 2;
    const receiptP = want([C.FRAME.RECEIPT, C.FRAME.ERROR], 24 * 60 * 60 * 1000, 'No receipt.');
    receiptP.catch(() => {});
    for (let fi = 0; fi < files.length; fi++) {
      const file = files[fi], h = sha256.create();
      const n = C.chunksFor(file.size);
      for (let ci = 0; ci < n; ci++) {
        if (done) return;
        const data = new Uint8Array(await file.slice(ci * CHUNK, (ci + 1) * CHUNK).arrayBuffer());
        h.update(data);
        while (dc.bufferedAmount > MAX_BUFFER && !done) await new Promise((r) => { dc.addEventListener('bufferedamountlow', r, { once: true }); setTimeout(r, 1000); });
        if (done) return;
        dc.send(await out.seal(C.FRAME.CHUNK, C.enc.chunk(fi, ci, data)));
      }
      const hex = bytesToHex(h.digest());
      hashes.push(hex);
      dc.send(await out.seal(C.FRAME.DONE, C.enc.json({ file: fi, sha256: hex })));
    }
    const rf = await receiptP;
    if (rf.type === C.FRAME.ERROR) {
      let msg = 'Their device reported a problem.'; try { const e = C.enc.fromJson(rf.body); if (typeof e.msg === 'string') msg = e.msg.slice(0, 200); } catch {}
      throw new Error(msg);
    }
    const receipt = C.enc.fromJson(rf.body);
    const ok = Array.isArray(receipt.hashes) && receipt.hashes.join(',') === hashes.join(',') && isB64(receipt.sig, 200)
      && C.verify(toPub, C.b64.dec(receipt.sig), 'drop/receipt', ...C.receiptPayload(tx, receipt.hashes));
    if (!ok) throw new Error('The receipt did not match what was sent.');
    done = true;
    cb.onDone?.({ tx, route, hashes, receiptSigned: true, total });
    setTimeout(cleanup, 1500);
  }
}

// ---------------------------------------------------------------- receiving
// Owner side.
// app.onRequest({from, words}) -> Promise<boolean>   asked BEFORE any network details are shared
// app.onIncoming(request)                            once the encrypted file list has arrived
// request: { from, words, manifest, tx, route, accept(), decline(), cancel(), on: {confirm, done, error} }
export function listen({ id, code, app, onState, onOther, relayOnly = () => false }) {
  const peers = new Set();
  // One live connection per sender. Released the moment a transfer finishes, is declined,
  // or the sender goes away before connecting, so they can send again straight away.
  const pendingFrom = new Map();
  const release = (key, entry) => { if (pendingFrom.get(key) === entry) pendingFrom.delete(key); };
  const seen = new Map(); // offer signatures already handled -> time (stops replays)
  const sig = new Signal({
    id, inbox: C.inboxId(id.pub), role: 'owner', code, onState,
    onMsg: (m) => {
      if (m.t === 'guest-left') {
        // The sender's app went away while we were still asking whether to connect: free its slot.
        // (Once a connection exists the sender closes its server link on purpose, so leave those alone.)
        for (const [k, e] of pendingFrom) if (e.from === m.from && !e.pc) { e.dead = true; pendingFrom.delete(k); }
        return;
      }
      if (m.t !== 'msg' || !m.data || typeof m.data !== 'object' || m.data.kind !== 'offer') { onOther?.(m); return; }
      handleOffer(m).catch(() => {});
    },
  });

  const reject = (to, why) => sig.send({ t: 'msg', to, data: { kind: 'reject', why } });

  async function handleOffer(m) {
    const d = m.data;
    if (typeof m.from !== 'string' || !isB64(m.pub, 100) || !isB64(d.eph, 100) || !isB64(d.nonce, 100) || !isB64(d.sig, 200) || typeof d.sdp !== 'string' || d.sdp.length > 30000 || !Number.isSafeInteger(d.ts)) return;
    if (d.v !== C.PROTOCOL) return reject(m.from, 'version');
    const fromPub = C.b64.dec(m.pub), ephA = C.b64.dec(d.eph), nonce = C.b64.dec(d.nonce);
    if (fromPub.length !== 32 || ephA.length !== 32 || nonce.length !== 16) return;
    if (Math.abs(Date.now() - d.ts) > OFFER_MAX_AGE) return;
    const now = Date.now();
    for (const [k, t] of seen) if (now - t > 2 * OFFER_MAX_AGE) seen.delete(k);
    if (seen.has(d.sig)) return;
    if (!C.verifyOffer(fromPub, id.pub, ephA, d.sdp, d.ts, nonce, C.b64.dec(d.sig))) return;
    seen.set(d.sig, now);
    const fromAddress = C.addressFromPub(fromPub);
    const fromKey = m.pub;
    const prev = pendingFrom.get(fromKey);
    if (prev && !prev.gotChannel) { prev.dead = true; try { prev.pc?.close(); } catch {} peers.delete(prev.pc); pendingFrom.delete(fromKey); } // a newer attempt replaces one that never connected
    if (pendingFrom.has(fromKey) || pendingFrom.size >= MAX_PENDING) return reject(m.from, 'busy');
    const entry = { from: m.from, pc: null, gotChannel: false, dead: false };
    pendingFrom.set(fromKey, entry);
    let pc;
    try {
      const ok = await Promise.race([app.onRequest({ from: fromAddress, words: C.checkWords(fromPub, id.pub) }), new Promise((r) => setTimeout(() => r(false), CONSENT_WAIT))]);
      if (entry.dead) return;
      if (!ok) return reject(m.from, 'no');
      if (sig.closed) return;
      const eph = C.newEphemeral();
      pc = new RTCPeerConnection(await rtcConfig(sig, relayOnly()));
      entry.pc = pc;
      if (entry.dead) { try { pc.close(); } catch {} return; }
      peers.add(pc);
      await pc.setRemoteDescription({ type: 'offer', sdp: d.sdp });
      await pc.setLocalDescription(await pc.createAnswer());
      await waitIce(pc);
      const sdp = pc.localDescription.sdp;
      const s = C.signAnswer(id, eph, fromPub, ephA, sdp);
      let gotChannel = false;
      pc.ondatachannel = (ev) => {
        if (gotChannel) return ev.channel.close();
        gotChannel = true; entry.gotChannel = true;
        receive(ev.channel, pc, { fromPub, fromAddress, eph, ephA }, () => release(fromKey, entry)).finally(() => { peers.delete(pc); release(fromKey, entry); });
      };
      sig.send({ t: 'msg', to: m.from, data: { kind: 'answer', eph: C.b64.enc(eph.pub), sdp, sig: C.b64.enc(s) } });
      setTimeout(() => { if (!gotChannel) { try { pc.close(); } catch {} peers.delete(pc); release(fromKey, entry); } }, 45000);
    } catch { try { pc?.close(); } catch {} peers.delete(pc); release(fromKey, entry); reject(m.from, 'no'); }
    finally { if (!pc || pc.signalingState === 'closed') release(fromKey, entry); }
  }

  function receive(dc, pc, { fromPub, fromAddress, eph, ephA }, released = () => {}) {
    return new Promise((settle) => {
      dc.binaryType = 'arraybuffer';
      const keys = C.sessionKeys(eph, ephA, fromPub, id.pub, ephA, eph.pub);
      let inn, out;
      const ready = Promise.all([new C.Cipher(keys.a2b, 1).init(), new C.Cipher(keys.b2a, 2).init()]).then(([i, o]) => { inn = i; out = o; });
      let manifest, tx, req, state = 'manifest', finished = false, cur = 0, received = 0;
      const parts = [], hashers = [], hashes = [], got = [], bytes = [];
      let idle = setTimeout(() => fail('Nothing arrived in time.'), 30000);
      const touch = (ms = IDLE_LIMIT) => { clearTimeout(idle); idle = setTimeout(() => fail('The transfer stalled and was stopped.'), ms); };
      const close = () => { clearTimeout(idle); try { dc.close(); } catch {} try { pc.close(); } catch {} settle(); };
      const fail = (msg) => { if (finished) return; finished = true; released(); parts.length = 0; req?.on?.error?.(new Error(msg)); close(); };
      dc.onclose = () => fail('The connection closed before the transfer finished.');
      let chain = Promise.resolve();
      dc.onmessage = (ev) => { chain = chain.then(async () => {
        if (finished) return;
        await ready;
        const f = await inn.open(new Uint8Array(ev.data));
        if (state === 'manifest' && f.type === C.FRAME.MANIFEST) {
          const mBytes = f.body.slice();
          manifest = C.validateManifest(C.enc.fromJson(mBytes));
          tx = C.txId(mBytes, ephA);
          manifest.files.forEach((file, i) => { parts[i] = []; hashers[i] = sha256.create(); got[i] = 0; bytes[i] = 0; });
          state = 'asking';
          touch(5 * 60 * 1000);
          const route = await routeOf(pc);
          req = {
            from: fromAddress, words: C.checkWords(fromPub, id.pub), manifest, tx, route, on: {},
            accept: async () => { if (state !== 'asking' || finished) return; state = 'receiving'; touch(); dc.send(await out.seal(C.FRAME.ACCEPT, new Uint8Array(0))); },
            decline: async () => { if (finished) return; finished = true; released(); try { dc.send(await out.seal(C.FRAME.DECLINE, new Uint8Array(0))); } catch {} setTimeout(close, 500); },
            cancel: () => fail('Cancelled.'),
          };
          app.onIncoming(req);
          return;
        }
        if (state === 'receiving' && f.type === C.FRAME.CHUNK) {
          const { fileIdx, chunkIdx, data } = C.enc.unchunk(f.body);
          const file = manifest.files[fileIdx];
          if (fileIdx !== cur || !file || chunkIdx !== got[fileIdx]) throw new Error('order');
          const last = chunkIdx === C.chunksFor(file.size) - 1;
          const expect = last ? file.size - chunkIdx * CHUNK : CHUNK;
          if (chunkIdx >= C.chunksFor(file.size) || data.length !== expect) throw new Error('size');
          const copy = data.slice();
          parts[fileIdx].push(copy); hashers[fileIdx].update(copy);
          got[fileIdx]++; bytes[fileIdx] += copy.length; received++;
          touch();
          dc.send(await out.seal(C.FRAME.ACK, new Uint8Array(0)));
          req.on.confirm?.(received, manifest.total);
          return;
        }
        if (state === 'receiving' && f.type === C.FRAME.DONE) {
          const { file, sha256: claimed } = C.enc.fromJson(f.body);
          const fm = manifest.files[file];
          if (file !== cur || !fm || got[file] !== C.chunksFor(fm.size) || bytes[file] !== fm.size || typeof claimed !== 'string') throw new Error('done');
          const digest = bytesToHex(hashers[file].digest());
          if (digest !== claimed) { try { dc.send(await out.seal(C.FRAME.ERROR, C.enc.json({ msg: 'A file did not match its fingerprint.' }))); } catch {} return fail('A file did not match its fingerprint and was thrown away.'); }
          hashes[file] = digest; cur++;
          if (cur === manifest.files.length) {
            const sigR = C.sign(id.secret, 'drop/receipt', ...C.receiptPayload(tx, hashes));
            dc.send(await out.seal(C.FRAME.RECEIPT, C.enc.json({ hashes, sig: C.b64.enc(sigR) })));
            finished = true; released(); clearTimeout(idle);
            const blobs = manifest.files.map((m, i) => new File(parts[i], m.name, { type: m.type }));
            req.on.done?.({ files: blobs, hashes, tx, route: req.route });
            setTimeout(close, 3000);
          }
          return;
        }
        throw new Error('unexpected');
      }).catch((e) => {
        if (finished) return;
        if (state === 'receiving') { out?.seal(C.FRAME.ERROR, C.enc.json({ msg: 'A message failed its security check.' })).then((b) => { try { dc.send(b); } catch {} }).catch(() => {}); }
        fail(e?.message && /version|too|impossible|name|list/i.test(e.message) && state === 'manifest' ? e.message : 'A message failed its security check. Transfer stopped.');
      }); };
    });
  }

  sig.connect().catch(() => {});
  return { stop: () => { sig.close(); peers.forEach((pc) => { try { pc.close(); } catch {} }); }, signal: sig };
}
